import { useEffect, useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MzButton from "@markaaz/ui/MzButton";
import MzAlert from "@markaaz/ui/MzAlert";
import MzDataGrid from "@markaaz/ui/MzDataGrid";
import LabelGrid from "./LabelGrid";

const sx = {
  dataGrid: {
    "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
      borderBottom: "none"
    },
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: "none"
    },
    "& .MuiDataGrid-columnHeader[data-field='TraceID']": {
      borderRight: (theme) => `2px solid ${theme.palette.blue.c5}`
    },
    "& .MuiDataGrid-cell[data-field='TraceID']": {
      borderRight: (theme) => `2px solid ${theme.palette.blue.c5}`
    },
    "& .MuiDataGrid-virtualScroller": {
      "&::-webkit-scrollbar": {
        width: "10px",
        height: "10px",
        backgroundColor: (theme) => `${theme.palette.common.white}`
      }
    },
    "& .MuiDataGrid-footerContainer": {
      minHeight: "0px"
    }
  },
  responseCode: {
    margin: "20px 0px 20px 0px"
  }
};

export default function TestMatch({
  loading,
  data,
  isFileUploaded,
  onRunMatch,
  loadData,
  showAlert,
  showRunTestError,
  fieldMappingRows,
  isError,
  onAlertClose
}) {
  const [showNoFile, setShowNoFile] = useState(false);

  useEffect(() => {
    if (showNoFile && isError) {
      setShowNoFile(false);
    }
  }, [showNoFile, isError]);

  useEffect(() => {
    if (showAlert.message) {
      setShowNoFile(false);
    }
  }, [showAlert.message]);

  function handleAlertClose() {
    setShowNoFile(false);
  }

  function handleRunTestMatch() {
    if (!isFileUploaded) {
      setShowNoFile(true);
      onAlertClose("smd");
      onAlertClose("fm");
    } else {
      onRunMatch();
    }
  }

  useEffect(() => {
    loadData && setShowNoFile(false);
  }, [loadData]);

  const generateColumns = () => {
    const metaDataColumns = [
      { field: "HTTP Response", headerName: "HTTP Response", width: 150, sortable: false },
      {
        field: "Message ID",
        headerName: "Message ID",
        width: 150,
        sortable: false,
        renderCell: ({ row, value }) => {
          return (
            <Box display="flex" className="MuiDataGrid-cellContent">
              <Box>
                {row["HTTP Response"] === 200 ? (
                  "-"
                ) : row["HTTP Response"] === 207 ? (
                  ""
                ) : row["HTTP Response"] === null ? (
                  ""
                ) : (
                  <ErrorIcon color="error" />
                )}
                &nbsp;
              </Box>
              <Box marginTop={1}> {value}</Box>
            </Box>
          );
        }
      },
      { field: "Matched", headerName: "Matched", width: 100, sortable: false },
      { field: "TraceID", headerName: "TraceID", width: 350, sortable: false }
    ];

    const filedMappingColumns = fieldMappingRows.map((field) => {
      const headerName = field.fieldOrder ? `${field.fieldOrder}. ${field.fieldName}` : field.fieldName;
      return {
        field: field.fieldName,
        headerName: headerName,
        width: 200,
        sortable: false
      };
    });

    return [...metaDataColumns, ...filedMappingColumns];
  };

  let errorRows = [];

  if (data) {
    errorRows = [
      {
        label: "Match Count",
        value: data.testSummary.matchCount
      },
      {
        label: "Non-Match Count",
        value: data.testSummary.nonMatchCount
      },
      {
        label: "Error Count",
        value: data.testSummary.errorCount
      }
    ];
  }

  return (
    <Box>
      <Typography variant="h6" mt={8} mb={2}>
        Test Match
      </Typography>
      <Typography variant="body2" mb={2} maxWidth={750}>
        Run a real-time match test calling our Match API to to validate the file format. The full data response for the
        test file will be available via SFTP when the job completes batch processing
      </Typography>

      <Typography variant="body2B" component={"p"} mb={2} maxWidth={750}>
        The test file is limited to 20 rows for data format validation of process records. Larger test files may be
        rejected or fail to load in the browser.
      </Typography>

      <Stack direction="row" alignItems="center" gap={4}>
        <MzButton sx={{ my: 8 }} loading={loading} size="large" variant="contained" onClick={handleRunTestMatch}>
          Run Test Match
        </MzButton>
        <Box>
          {showNoFile && (
            <MzAlert
              sx={{ maxWidth: "320px", alignItems: "center" }}
              severity={"error"}
              action={
                <IconButton color="inherit" size="small" onClick={handleAlertClose}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              Unable to run test match, please upload a test file first.
            </MzAlert>
          )}
        </Box>
      </Stack>

      {data && loadData && (
        <>
          <Box mb={5}>
            <Typography variant="body1B">Match Summary</Typography>
            <Typography mt={2} variant="body2">
              Refer to your SFTP location to pull the full report of the test.
            </Typography>
            {showRunTestError && (
              <MzAlert
                severity="error"
                sx={{ mt: 5 }}
                onClose={() => {
                  onAlertClose("rtm");
                }}
              >
                {data.testSummary.errorReason
                  ? data.testSummary.errorReason
                  : "We detected an error in the file, please review the inputs in the file to ensure they are accurate."}
              </MzAlert>
            )}
          </Box>
          <Box>
            <Box height={400}>
              <MzDataGrid
                sx={sx.dataGrid}
                variant="3"
                rowHeight={56}
                rows={data.testMatchData}
                columns={generateColumns()}
                disableSelectionOnClick
                disableColumnMenu={true}
                hideFooterPagination
              />
            </Box>
            {Object.values(data.testSummary).every((value) => value !== null) && (
              <Box>
                <Typography variant="body2" sx={sx.responseCode}>
                  You can learn about
                  <MzButton
                    href="https://developer.markaaz.com/docs/http-response-codes-v20"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Typography variant="button2"> response and error codes.</Typography>
                  </MzButton>
                </Typography>
                <LabelGrid rows={errorRows}></LabelGrid>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
